import React, { forwardRef } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  showPasswordToggle?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      error,
      helperText,
      containerClassName = '',
      labelClassName = '',
      inputClassName = '',
      showPasswordToggle = false,
      startIcon,
      endIcon,
      type: inputType,
      id,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const actualType = showPasswordToggle && showPassword ? 'text' : inputType;

    return (
      <div className={`space-y-2 ${containerClassName}`}>
        {label && (
          <label
            htmlFor={id}
            className={`block text-sm font-medium ${labelClassName}`}
            style={{ color: error ? '#E74C3C' : 'var(--patreon-black)' }}
          >
            {label}
          </label>
        )}

        <div className="relative">
          {startIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {startIcon}
            </div>
          )}

          <input
            ref={ref}
            type={actualType}
            id={id}
            className={`
              block w-full rounded-[var(--patreon-radius-lg)] border px-4 py-3
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-offset-0
              disabled:bg-gray-100 disabled:border-gray-300 disabled:cursor-not-allowed
              placeholder:text-gray-400
              ${error ? 'border-[color:var(--patreon-error)] focus:border-[color:var(--patreon-error)] focus:ring-[color:var(--patreon-error)]/20' : 'border-[color:var(--patreon-neutral-30)] focus:border-[color:var(--patreon-orange)] focus:ring-[color:var(--patreon-orange)]/10'}
              ${startIcon ? 'pl-10' : ''}
              ${endIcon || showPasswordToggle ? 'pr-10' : ''}
              ${inputClassName}
              hover:border-[color:var(--patreon-neutral-40)]
            `}
            style={{
              background: 'var(--patreon-card-bg)',
              color: 'var(--patreon-black)',
              fontFamily: 'var(--patreon-font-primary)',
            }}
            {...props}
          />

          {(endIcon || showPasswordToggle) && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {showPasswordToggle ? (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              ) : (
                endIcon
              )}
            </div>
          )}
        </div>

        {error && (
          <p className="text-sm" style={{ color: 'var(--patreon-error)' }}>{error}</p>
        )}

        {helperText && !error && (
          <p className="text-sm" style={{ color: 'var(--patreon-gray)' }}>{helperText}</p>
        )}
      </div>
    );
  }
);

FormInput.displayName = 'FormInput';

export default FormInput;
